import { supabase, applyOrgFilter, getActiveOrgOverride, DEFAULT_ORG_ID, signImagesDeep } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { computeEntregableEstatus } from './pagosEntregables'
import { getJugadoresInscritos } from './temporadaInscripciones'
import { calcularDescuento } from './descuentoPromociones'
import { calcularAjustesCargos, calcularCargosFaltantes } from './pagosPlanHelpers'
import { buscarPersonaPorTelefono, crearPersona, ligarPersonaAFamilia } from './personas'
import { responsableParaCuenta, EMBED_RESPONSABLE, conResponsable } from '../familias'
import { apellidoPaterno } from '../nombres'
import { partirSinPerder, mismoTexto, soloDigitos } from '../responsableResolver'

// =====================================================
// FAMILIAS
// =====================================================

// Unifica cuentas: mueve jugadores, saldo a favor, solicitudes de pago y
// consentimientos a la cuenta destino, y desactiva las de origen.
//
// Va por RPC y no por varios updates desde el cliente porque `familias` la
// referencian CUATRO tablas, dos de ellas con dinero. Un merge a medias -- jugadores
// movidos pero el saldo apuntando a una cuenta desactivada -- es peor que no
// haberlo intentado: es dinero de una familia que nadie va a encontrar.
export const unificarCuentas = async (destinoId, origenIds = []) => {
  try {
    if (!destinoId) return { data: null, error: new Error('Falta la cuenta destino.') }
    const origenes = (origenIds || []).filter((id) => id && id !== destinoId)
    if (!origenes.length) return { data: null, error: new Error('Selecciona al menos una cuenta a unificar.') }

    const { data, error } = await supabase.rpc('unificar_cuentas', {
      p_destino: destinoId,
      p_origenes: origenes,
    })
    if (error) throw error
    if (data?.error) {
      const msg = {
        destino_inexistente: 'La cuenta destino ya no existe.',
        sin_acceso: 'No tienes acceso a esta cuenta.',
        origen_de_otra_org: 'Alguna de las cuentas pertenece a otra organizacion.',
        destino_en_origenes: 'La cuenta destino no puede estar entre las que se unifican.',
      }[data.error] || 'No se pudo unificar.'
      return { data: null, error: new Error(msg) }
    }
    return { data, error: null }
  } catch (error) {
    console.error('[unificarCuentas]', error)
    return { data: null, error }
  }
}

export const getFamilias = async (orgId = null) => {
  try {
    let query = supabase
      .from('familias')
      .select(`
        *,
        ${EMBED_RESPONSABLE},
        jugadores:jugadores!familia_id(id, nombre_completo, numero, imagen_path, imagen_url, estatus, genero)
      `)
      .eq('activo', true)
      .order('nombre')

    query = applyOrgFilter(query, orgId)
    const { data, error } = await query
    if (error) throw error
    await signImagesDeep(data)
    // `responsable` sale del vinculo con la persona; familias.tutor_* ya no existe.
    return { data: (data || []).map(conResponsable), error: null }
  } catch (error) {
    console.error('[getFamilias]', error)
    return { data: [], error }
  }
}

export const createFamilia = async (familiaData) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    // El PIN se genera/recibe en claro aqui, pero la BD solo guarda el hash
    // (trigger trg_hash_familia_pin). Se devuelve `pin_plain` para mostrarlo
    // UNA sola vez al admin; no vuelve a estar disponible despues.
    const pinPlain = /^\d{4}$/.test(familiaData.pin || '')
      ? familiaData.pin
      : String(Math.floor(1000 + Math.random() * 9000))

    // Sin tutor_*: esas columnas son un espejo de `personas` y la base rechaza
    // escribirlas (20260908b). El responsable se liga como persona (familia_personas)
    // y el espejo las llena solo. Ver crearFamiliaConResponsable / guardarResponsableDeFamilia.
    const { data, error } = await supabase
      .from('familias')
      .insert({
        org_id: orgId,
        nombre: familiaData.nombre,
        pin: pinPlain,
        notas: familiaData.notas || null,
      })
      .select()
      .single()

    if (error) throw error
    return { data: { ...data, pin_plain: pinPlain }, error: null }
  } catch (error) {
    console.error('[createFamilia]', error)
    return { data: null, error }
  }
}

// Regenera el PIN de una familia. La BD guarda solo el hash; se devuelve
// `pin_plain` para mostrarlo UNA vez al admin.
export const regenerarPinFamilia = async (id) => {
  try {
    const pinPlain = String(Math.floor(1000 + Math.random() * 9000))
    const { data, error } = await supabase
      .from('familias')
      .update({ pin: pinPlain })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data: { ...data, pin_plain: pinPlain }, error: null }
  } catch (error) {
    console.error('[regenerarPinFamilia]', error)
    return { data: null, error }
  }
}

// Crea una "cuenta" (familia) de un solo jugador y lo asigna, para poder
// compartirle su estado de cuenta. Devuelve la familia con pin_plain + access_token.
// Son DOS escrituras y la segunda puede fallar sola: la familia se crea (nadie lo
// impide) y la asignacion del jugador la filtra el RLS. Ignorar ese resultado dejaba
// una familia huerfana por cada intento y reportaba exito. Si no se pudo asignar, se
// deshace lo creado -- una familia vacia que nadie pidio es basura que despues aparece
// en Pagos y en esta misma lista como "familia sin responsable".
// Crea una familia con su responsable y le asigna jugadores. Es el nucleo comun de
// "crear cuenta" (un jugador) y de la importacion (hermanos de un archivo).
//
// Sigue el camino del alta: persona -> familia -> vinculo -> jugadores. Con
// `familiaId` no crea nada: los jugadores se suman a esa familia (el responsable ya
// existe y ya responde ahi). Con `personaId` reusa a esa persona sin escribirle nada.
// Sin ninguno, la persona se reusa solo con mismo telefono Y mismo nombre; si no,
// se crea. Corregir identidades es de Fichas por completar, no de aqui.
//
// Devuelve { familiaId, creada, asignados }. Si no se pudo asignar a NADIE, la
// familia recien creada se deshace: una familia vacia que nadie pidio es basura que
// despues aparece como "familia sin responsable".
export const crearFamiliaConResponsable = async ({
  responsable = null, jugadorIds = [], nombreFallback = '', personaId = null, familiaId = null,
}) => {
  try {
    if (familiaId) {
      let asignados = 0
      for (const id of jugadorIds) {
        const { error } = await assignPlayerToFamilia(id, familiaId)
        if (!error) asignados += 1
      }
      return { data: { familiaId, creada: false, asignados }, error: null }
    }

    let persona = personaId
    if (!persona && responsable?.nombre) {
      if (soloDigitos(responsable.telefono).length >= 10) {
        const { data: candidatos } = await buscarPersonaPorTelefono(responsable.telefono)
        persona = (candidatos || []).find((p) => mismoTexto(p.nombre_completo, responsable.nombre))?.id || null
      }
      if (!persona) {
        const partes = partirSinPerder(responsable.nombre)
        const { data: nueva, error: errPersona } = await crearPersona({
          nombres: partes.nombres, apellidos: partes.apellidos,
          telefono: responsable.telefono, email: responsable.email,
        })
        if (errPersona) throw errPersona
        persona = nueva?.id || null
      }
    }

    const apellidos = responsable ? partirSinPerder(responsable.nombre).apellidos : ''
    const { data: familia, error } = await createFamilia({
      nombre: responsable?.esElJugador || !apellidoPaterno(apellidos)
        ? (nombreFallback || responsable?.nombre || 'Familia')
        : `Familia ${apellidoPaterno(apellidos)}`,
    })
    if (error) throw error

    // El vinculo es lo que la vuelve la familia de esa persona; el espejo copia sus
    // datos a familias.tutor_*. Si falla, la familia queda sin responsable y aparece
    // asi en Fichas por completar: no se pierde, y nadie escribe un texto suelto.
    if (persona) {
      const { error: errLigar } = await ligarPersonaAFamilia({ familiaId: familia.id, personaId: persona, rol: 'responsable' })
      if (errLigar) console.error('[crearFamiliaConResponsable] ligar', errLigar)
    }

    let asignados = 0
    let ultimoError = null
    for (const id of jugadorIds) {
      const { error: errAsignar } = await assignPlayerToFamilia(id, familia.id)
      if (errAsignar) ultimoError = errAsignar; else asignados += 1
    }
    if (jugadorIds.length > 0 && asignados === 0) {
      // La compensacion es "lo mejor que se pueda": si tampoco se puede borrar, al
      // menos se desactiva para que no aparezca. Lo que NO se hace es callar el fallo.
      const { error: errBorrar } = await supabase.from('familias').delete().eq('id', familia.id)
      if (errBorrar) await supabase.from('familias').update({ activo: false }).eq('id', familia.id)
      throw ultimoError
    }
    return { data: { ...familia, familiaId: familia.id, creada: true, asignados }, error: null }
  } catch (error) {
    console.error('[crearFamiliaConResponsable]', error)
    return { data: null, error }
  }
}

export const crearCuentaParaJugador = async (jugador) => {
  try {
    // La ficha se lee de la base, no del llamador: Estado de cuenta manda la fila de
    // la lista y Fichas por completar solo manda { id, nombre_completo }. Los campos
    // que deciden quien responde viven en la ficha.
    const { data: ficha, error: errFicha } = await supabase
      .from('jugadores')
      .select('id, nombre_completo, nombres, apellidos, contacto, telefono_contacto, email_tutor, telefono_jugador, fecha_nacimiento')
      .eq('id', jugador.id)
      .single()
    if (errFicha) throw errFicha

    // Antes se creaba la familia vacia y con `jugador.telefono`, un campo que la ficha
    // no tiene, asi que nacia sin responsable y caia en Fichas por completar.
    const { data, error } = await crearFamiliaConResponsable({
      responsable: responsableParaCuenta(ficha),
      jugadorIds: [jugador.id],
      nombreFallback: ficha.nombre_completo,
    })
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[crearCuentaParaJugador]', error)
    return { data: null, error }
  }
}

export const desbloquearFamilia = async (id) => {
  try {
    const { data, error } = await supabase
      .from('familias')
      .update({ pin_intentos: 0, pin_bloqueado_hasta: null })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[desbloquearFamilia]', error)
    return { data: null, error }
  }
}

export const updateFamilia = async (id, updates) => {
  try {
    // Sin tutor_*: son espejo de `personas` y la base rechaza escribirlas (20260908b).
    // Corregir al responsable es guardarResponsableDeFamilia().
    const ALLOWED = ['nombre', 'notas', 'activo']
    const safe = {}
    for (const k of ALLOWED) if (k in updates) safe[k] = updates[k]

    const { data, error } = await supabase
      .from('familias')
      .update(safe)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updateFamilia]', error)
    return { data: null, error }
  }
}

export const regenerarTokenFamilia = async (id) => {
  try {
    const { data, error } = await supabase
      .from('familias')
      .update({ access_token: crypto.randomUUID() })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[regenerarTokenFamilia]', error)
    return { data: null, error }
  }
}

// CERO FILAS NO ES EXITO. Un UPDATE que el RLS filtra no devuelve error: devuelve
// cero filas y `error: null`. Sin esta comprobacion, quien no puede escribir sobre ese
// jugador (el caso real: `staff.rol='admin'` pero la membresia en la org activa es
// `coach`, que es lo que mira `is_org_admin()`) veia cerrarse el modal y recargarse la
// lista con el jugador todavia ahi, sin una sola palabra.
export const assignPlayerToFamilia = async (jugadorId, familiaId) => {
  try {
    const { data, error } = await supabase
      .from('jugadores')
      .update({ familia_id: familiaId })
      .eq('id', jugadorId)
      .select('id, familia_id')

    if (error) throw error
    if (!data || data.length === 0) {
      throw new Error(
        'No se pudo cambiar la familia de ese jugador: puede que no tengas permiso de administrador en esta organización, o que el jugador ya no exista.'
      )
    }
    return { data: data[0], error: null }
  } catch (error) {
    console.error('[assignPlayerToFamilia]', error)
    return { data: null, error }
  }
}

export const removePlayerFromFamilia = async (jugadorId) => {
  return assignPlayerToFamilia(jugadorId, null)
}

// =====================================================
// CONCEPTOS
// =====================================================

export const getConceptos = async (orgId = null) => {
  try {
    let query = supabase
      .from('pagos_conceptos')
      .select('*')
      .order('tipo')
      .order('nombre')

    query = applyOrgFilter(query, orgId)
    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getConceptos]', error)
    return { data: [], error }
  }
}

export const createConcepto = async (conceptoData) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const { data, error } = await supabase
      .from('pagos_conceptos')
      .insert({
        org_id: orgId,
        nombre: conceptoData.nombre,
        tipo: conceptoData.tipo || 'otro',
        monto_default: conceptoData.monto_default || null,
        descripcion: conceptoData.descripcion || null,
      })
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[createConcepto]', error)
    return { data: null, error }
  }
}

export const updateConcepto = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('pagos_conceptos')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updateConcepto]', error)
    return { data: null, error }
  }
}

export const deleteConcepto = async (id) => {
  try {
    const { error } = await supabase
      .from('pagos_conceptos')
      .update({ activo: false })
      .eq('id', id)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[deleteConcepto]', error)
    return { error }
  }
}

// ¿El concepto está en uso? (planes ACTIVOS/parcialidades o cargos no cancelados)
// Solo cuentan los planes activos: un concepto usado unicamente en planes borrados
// (activo=false, versiones viejas) NO debe bloquear su eliminacion. Los cargos no
// cancelados si bloquean (son dinero real, aunque su plan este inactivo).
export const conceptoEnUso = async (conceptoId) => {
  try {
    const [items, parc, cargos] = await Promise.all([
      supabase.from('pagos_planes_items').select('id, pagos_planes!inner(activo)', { count: 'exact', head: true }).eq('concepto_id', conceptoId).eq('pagos_planes.activo', true),
      supabase.from('pagos_planes_parcialidades').select('id, pagos_planes!inner(activo)', { count: 'exact', head: true }).eq('concepto_id', conceptoId).eq('pagos_planes.activo', true),
      supabase.from('pagos_cargos').select('id', { count: 'exact', head: true }).eq('concepto_id', conceptoId).neq('estatus', 'cancelado'),
    ])
    const planes = (items.count || 0) + (parc.count || 0)
    const cargosCount = cargos.count || 0
    return { enUso: planes + cargosCount > 0, planes, cargos: cargosCount, error: null }
  } catch (error) {
    console.error('[conceptoEnUso]', error)
    return { enUso: false, planes: 0, cargos: 0, error }
  }
}

// Resumen de cargos de un plan (para avisar al eliminarlo)
export const getResumenCargosPlan = async (planId) => {
  try {
    const { data, error } = await supabase
      .from('pagos_cargos')
      .select('estatus')
      .eq('plan_id', planId)
      .neq('estatus', 'cancelado')
    if (error) throw error
    const total = (data || []).length
    const pagados = (data || []).filter((c) => c.estatus === 'pagado').length
    return { total, pagados, noPagados: total - pagados, error: null }
  } catch (error) {
    console.error('[getResumenCargosPlan]', error)
    return { total: 0, pagados: 0, noPagados: 0, error }
  }
}

// Recalcula los cargos de un plan a los nuevos montos de sus parcialidades.
// mode 'pendiente' = solo cargos sin pagos; 'total' = todos (sobrante -> saldo a favor).
// dryRun = solo calcula el impacto, no escribe. NO toca monto_pagado (lo cobrado).
export const recalcularCargosPlan = async (planId, mode = 'total', { dryRun = false } = {}) => {
  try {
    const { data: parcs, error: pErr } = await supabase
      .from('pagos_planes_parcialidades').select('id, monto').eq('plan_id', planId)
    if (pErr) throw pErr
    const parcMonto = {}
    ;(parcs || []).forEach((p) => { parcMonto[p.id] = Number(p.monto || 0) })

    const { data: cargos, error: cErr } = await supabase
      .from('pagos_cargos')
      .select('id, jugador_id, parcialidad_id, monto_final, monto_pagado, descuento_porcentaje, temporada_id, jugadores:jugador_id(nombre_completo)')
      .eq('plan_id', planId)
      .neq('estatus', 'cancelado')
    if (cErr) throw cErr

    // La aritmetica vive en calcularAjustesCargos (puro y probado): la comparte
    // con el aviso de "cambios sin aplicar" de la pantalla de Planes.
    const cambios = calcularAjustesCargos(parcMonto, cargos, mode)
    const saldoAFavor = Math.round(cambios.reduce((s, c) => s + c.sobrante, 0) * 100) / 100

    if (dryRun) {
      return { data: { total: cambios.length, saldoAFavor, afectados: cambios.map((c) => ({ nombre: c.nombre, antes: c.antes, despues: c.despues })) }, error: null }
    }

    for (const ch of cambios) {
      await supabase.from('pagos_cargos').update({
        monto_original: ch.nuevoOriginal,
        descuento_monto: ch.descMonto,
        monto_final: ch.despues,
        saldo: ch.nuevoSaldo,
        estatus: ch.estatus,
      }).eq('id', ch.id)
      if (ch.sobrante > 0) {
        await crearSaldoFavor({ jugadorId: ch.jugador_id, monto: ch.sobrante, origen: 'ajuste_plan', descripcion: 'Ajuste por recálculo del plan', cargoOrigenId: ch.id, temporadaId: ch.temporada_id })
      }
    }
    return { data: { total: cambios.length, saldoAFavor }, error: null }
  } catch (error) {
    console.error('[recalcularCargosPlan]', error)
    return { data: null, error }
  }
}

// Cuantos cargos quedarian ajustados en cada plan si se aplicaran los montos
// VIGENTES de sus parcialidades. Alimenta la marca "cambios sin aplicar" de la
// pantalla de Planes: se compara al vuelo contra la misma funcion que ejecuta el
// recalculo, asi que es imposible que la marca y el efecto real discrepen. Una
// sola consulta para toda la pantalla, no una por plan.
// Quien esta asignado a estos planes, ya filtrado por el padron de la temporada
// EXACTAMENTE como lo hace asignarPlan: si la vista previa contara a alguien que
// la emision va a saltarse, mentiria.
const getAsignadosVigentes = async (planIds, temporadaId) => {
  const { data: asig, error } = await supabase
    .from('pagos_jugador_plan')
    .select('plan_id, jugador_id, jugadores:jugador_id(nombre_completo)')
    .in('plan_id', planIds)
    .eq('activo', true)
  if (error) throw error

  const inscritos = new Set(await getJugadoresInscritos(temporadaId))
  const porPlan = {}
  ;(asig || []).forEach((a) => {
    if (!inscritos.has(a.jugador_id)) return
    if (!porPlan[a.plan_id]) porPlan[a.plan_id] = []
    porPlan[a.plan_id].push({ jugador_id: a.jugador_id, nombre: a.jugadores?.nombre_completo || 'Jugador' })
  })
  return porPlan
}

// Que tan lejos quedaron los cargos del plan que dicen cobrar. Dos huecos
// distintos, contados por separado:
//   montos    -> el cargo existe pero con el importe viejo
//   faltantes -> el cargo nunca se emitio (pago agregado a un plan que ya cobraba)
//
// Todo se compara al vuelo contra las MISMAS funciones que ejecutan la correccion,
// asi que la marca no puede discrepar del efecto. Consultas por pantalla, no por plan.
export const getDesfaseDePlanes = async (planIds = [], temporadaId = null) => {
  try {
    const ids = [...new Set(planIds)].filter(Boolean)
    if (!ids.length) return {}

    const { data: parcs, error: pErr } = await supabase
      .from('pagos_planes_parcialidades').select('id, plan_id, nombre, monto').in('plan_id', ids)
    if (pErr) throw pErr
    const parcMonto = {}
    const parcsPorPlan = {}
    ;(parcs || []).forEach((p) => {
      parcMonto[p.id] = Number(p.monto || 0)
      if (!parcsPorPlan[p.plan_id]) parcsPorPlan[p.plan_id] = []
      parcsPorPlan[p.plan_id].push(p)
    })

    const { data: cargos, error: cErr } = await supabase
      .from('pagos_cargos')
      .select('id, plan_id, jugador_id, parcialidad_id, monto_final, monto_pagado, descuento_porcentaje, estatus')
      .in('plan_id', ids)
    if (cErr) throw cErr

    const vivos = (cargos || []).filter((c) => c.estatus !== 'cancelado')
    const out = {}
    const bucket = (id) => (out[id] = out[id] || { montos: 0, faltantes: 0 })

    calcularAjustesCargos(parcMonto, vivos, 'total').forEach((ch) => { bucket(ch.plan_id).montos++ })

    if (temporadaId) {
      const asignados = await getAsignadosVigentes(ids, temporadaId)
      const cargosPorPlan = {}
      ;(cargos || []).forEach((c) => { (cargosPorPlan[c.plan_id] = cargosPorPlan[c.plan_id] || []).push(c) })
      ids.forEach((planId) => {
        const { faltantes } = calcularCargosFaltantes({
          asignados: asignados[planId] || [],
          parcialidades: parcsPorPlan[planId] || [],
          cargos: cargosPorPlan[planId] || [],
        })
        if (faltantes.length) bucket(planId).faltantes += faltantes.length
      })
    }
    return out
  } catch (error) {
    // Fail-open a proposito: sin marca es mejor que una marca falsa.
    console.error('[getDesfaseDePlanes]', error)
    return {}
  }
}

// Detalle de los cargos que faltan por emitir en UN plan, para la vista previa.
export const getCargosFaltantesPlan = async (planId, temporadaId) => {
  try {
    const [{ data: parcialidades }, { data: cargos }, asignados] = await Promise.all([
      supabase.from('pagos_planes_parcialidades').select('id, nombre, monto').eq('plan_id', planId),
      supabase.from('pagos_cargos').select('jugador_id, parcialidad_id, estatus').eq('plan_id', planId),
      getAsignadosVigentes([planId], temporadaId),
    ])
    const { faltantes, omitidosPorCancelacion } = calcularCargosFaltantes({
      asignados: asignados[planId] || [],
      parcialidades: parcialidades || [],
      cargos: cargos || [],
    })
    const monto = Math.round(faltantes.reduce((sum, f) => sum + f.monto, 0) * 100) / 100
    const jugadores = [...new Set(faltantes.map((f) => f.jugador_id))].length
    return { data: { total: faltantes.length, jugadores, monto, omitidosPorCancelacion, faltantes }, error: null }
  } catch (error) {
    console.error('[getCargosFaltantesPlan]', error)
    return { data: null, error }
  }
}

// =====================================================
// PLANES
// =====================================================

export const getPlanes = async ({ temporadaId = null, orgId = null } = {}) => {
  try {
    let query = supabase
      .from('pagos_planes')
      .select(`
        *,
        pagos_planes_items(
          *,
          pagos_conceptos(id, nombre, tipo)
        ),
        pagos_planes_parcialidades(*),
        pagos_planes_entregables(*),
        pagos_cargos(count),
        temporadas:temporada_id(id, nombre)
      `)
      .eq('activo', true)
      .order('created_at', { ascending: false })

    query = applyOrgFilter(query, orgId)
    if (temporadaId) query = query.eq('temporada_id', temporadaId)

    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getPlanes]', error)
    return { data: [], error }
  }
}

export const createPlan = async (planData) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const { data, error } = await supabase
      .from('pagos_planes')
      .insert({
        org_id: orgId,
        temporada_id: planData.temporada_id || null,
        nombre: planData.nombre,
        descripcion: planData.descripcion || null,
        // 'libre' y no 'todos': la migracion 20260818b migra el valor legado y su
        // CHECK nuevo lo rechaza. Con el default viejo aqui no existia orden de
        // despliegue seguro (migrar primero rompe el codigo viejo, desplegar
        // primero choca con el CHECK viejo).
        scope: planData.scope || 'libre',
      })
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[createPlan]', error)
    return { data: null, error }
  }
}

export const updatePlan = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('pagos_planes')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updatePlan]', error)
    return { data: null, error }
  }
}

export const deletePlan = async (id) => {
  try {
    const { error } = await supabase
      .from('pagos_planes')
      .update({ activo: false })
      .eq('id', id)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[deletePlan]', error)
    return { error }
  }
}

export const addPlanItem = async (planId, itemData) => {
  try {
    const { data, error } = await supabase
      .from('pagos_planes_items')
      .insert({
        plan_id: planId,
        concepto_id: itemData.concepto_id,
        monto: itemData.monto,
        fecha_vencimiento: itemData.fecha_vencimiento || null,
        orden: itemData.orden || 0,
        descripcion: itemData.descripcion || null,
      })
      .select(`*, pagos_conceptos(id, nombre, tipo)`)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[addPlanItem]', error)
    return { data: null, error }
  }
}

export const updatePlanItem = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('pagos_planes_items')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updatePlanItem]', error)
    return { data: null, error }
  }
}

// Deja las lineas de concepto del plan iguales a la lista, SIN borrar y recrear.
//
// Borrar y recrear era imposible: pagos_cargos.plan_item_id apunta a estos
// renglones con FK NO ACTION, asi que la base rechaza borrar un renglon que ya
// genero cargos. El codigo viejo lo hacia igual e IGNORABA el error -- ese fallo
// silencioso, seguido del insert, es exactamente lo que duplicaba las lineas.
//
// Se empareja por concepto_id (hay una linea por concepto). Un renglon repetido
// sobrante se borra si puede; si tiene cargos colgando se deja en $0, que
// preserva el total sin violar la FK ni perder el rastro del cargo.
export const reconcilePlanItems = async (planId, items = []) => {
  try {
    const { data: actuales, error: curErr } = await supabase
      .from('pagos_planes_items')
      .select('id, concepto_id')
      .eq('plan_id', planId)
    if (curErr) throw curErr

    const porConcepto = new Map()
    ;(actuales || []).forEach((it) => {
      if (!porConcepto.has(it.concepto_id)) porConcepto.set(it.concepto_id, [])
      porConcepto.get(it.concepto_id).push(it.id)
    })

    const quedan = new Set(items.map((c) => c.concepto_id))
    const sobrantes = []
    const conceptosRetirados = []
    porConcepto.forEach((ids, conceptoId) => {
      if (quedan.has(conceptoId)) sobrantes.push(...ids.slice(1))
      else { sobrantes.push(...ids); conceptosRetirados.push(conceptoId) }
    })

    let bloqueados = new Set()
    if (sobrantes.length) {
      const { data: conCargo, error: cErr } = await supabase
        .from('pagos_cargos').select('plan_item_id').in('plan_item_id', sobrantes)
      if (cErr) throw cErr
      bloqueados = new Set((conCargo || []).map((c) => c.plan_item_id))

      // Quitar del plan un concepto que YA cobro no es un detalle a resolver
      // solo: se avisa, igual que con las parcialidades.
      const retiradoConCargos = conceptosRetirados.some((cid) =>
        (porConcepto.get(cid) || []).some((id) => bloqueados.has(id)))
      if (retiradoConCargos) {
        return { error: new Error('No puedes quitar del plan un concepto que ya tiene cargos generados. Cancela esos cargos primero.') }
      }

      const borrables = sobrantes.filter((id) => !bloqueados.has(id))
      if (borrables.length) {
        const { error: delErr } = await supabase.from('pagos_planes_items').delete().in('id', borrables)
        if (delErr) throw delErr
      }
      // Los que no se pueden borrar quedan en $0: siguen existiendo para el
      // cargo que los referencia, pero ya no suman al total del plan.
      const enCero = sobrantes.filter((id) => bloqueados.has(id))
      if (enCero.length) {
        const { error: zErr } = await supabase.from('pagos_planes_items').update({ monto: 0 }).in('id', enCero)
        if (zErr) throw zErr
      }
    }

    let orden = 1
    for (const c of items) {
      const existentes = (porConcepto.get(c.concepto_id) || []).filter((id) => !sobrantes.includes(id))
      const row = { concepto_id: c.concepto_id, monto: Number(c.monto || 0), orden: orden++ }
      if (existentes.length) {
        const { error } = await supabase.from('pagos_planes_items').update(row).eq('id', existentes[0])
        if (error) throw error
      } else {
        const { error } = await supabase.from('pagos_planes_items').insert({ plan_id: planId, ...row })
        if (error) throw error
      }
    }
    return { error: null }
  } catch (error) {
    console.error('[reconcilePlanItems]', error)
    return { error }
  }
}

export const removePlanItem = async (id) => {
  try {
    const { error } = await supabase
      .from('pagos_planes_items')
      .delete()
      .eq('id', id)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[removePlanItem]', error)
    return { error }
  }
}

// =====================================================
// PARCIALIDADES
// =====================================================

export const getParcialidades = async (planId) => {
  try {
    const { data, error } = await supabase
      .from('pagos_planes_parcialidades')
      .select('*')
      .eq('plan_id', planId)
      .order('orden', { ascending: true })
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getParcialidades]', error)
    return { data: [], error }
  }
}

// Reconciliación inteligente: actualiza parcialidades en sitio (sin borrar las
// que ya tienen cargos), inserta nuevas y borra solo las que el usuario quitó y
// están libres. Si intenta quitar una con cargos, devuelve error (no descuadrar).
export const reconcileParcialidades = async (planId, lista = [], { prendaConceptoIds = [] } = {}) => {
  try {
    const prendaSet = new Set(prendaConceptoIds)
    const { data: actuales, error: curErr } = await supabase
      .from('pagos_planes_parcialidades')
      .select('id, concepto_id')
      .eq('plan_id', planId)
    if (curErr) throw curErr

    // Las parcialidades de prenda (uniformes) las gestiona Uniformes: aquí no se tocan.
    const actualesGest = (actuales || []).filter((p) => !prendaSet.has(p.concepto_id))
    const listaGest = lista.filter((p) => !prendaSet.has(p.concepto_id))
    const actualesIds = new Set(actualesGest.map((p) => p.id))
    const listaIds = new Set(listaGest.map((p) => p.id).filter(Boolean))
    const aEliminar = actualesGest.map((p) => p.id).filter((id) => !listaIds.has(id))

    if (aEliminar.length) {
      const { data: conCargo, error: cErr } = await supabase
        .from('pagos_cargos')
        .select('parcialidad_id')
        .in('parcialidad_id', aEliminar)
        .neq('estatus', 'cancelado')
      if (cErr) throw cErr
      if ((conCargo || []).length) {
        return { error: new Error('No puedes eliminar parcialidades que ya tienen cargos generados. Cancela esos cargos primero o crea un plan nuevo.') }
      }
      const { error: delErr } = await supabase
        .from('pagos_planes_parcialidades').delete().in('id', aEliminar)
      if (delErr) throw delErr
    }

    for (const p of listaGest) {
      const row = {
        orden: p.orden || 0,
        nombre: p.nombre,
        monto: Number(p.monto || 0),
        fecha_limite: p.fecha_limite || null,
        concepto_id: p.concepto_id || null,
      }
      if (p.id && actualesIds.has(p.id)) {
        const { error } = await supabase.from('pagos_planes_parcialidades').update(row).eq('id', p.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('pagos_planes_parcialidades').insert({ plan_id: planId, ...row })
        if (error) throw error
      }
    }
    return { error: null }
  } catch (error) {
    console.error('[reconcileParcialidades]', error)
    return { error }
  }
}

export const replaceParcialidades = async (planId, lista = []) => {
  try {
    const { error: delError } = await supabase
      .from('pagos_planes_parcialidades')
      .delete()
      .eq('plan_id', planId)
    if (delError) throw delError

    if (!lista.length) return { data: [], error: null }

    const rows = lista.map((p) => ({
      plan_id: planId,
      orden: p.orden || 0,
      nombre: p.nombre,
      monto: Number(p.monto),
      fecha_limite: p.fecha_limite || null,
      concepto_id: p.concepto_id || null,
    }))
    const { data, error } = await supabase
      .from('pagos_planes_parcialidades')
      .insert(rows)
      .select()
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[replaceParcialidades]', error)
    return { data: [], error }
  }
}

export const addParcialidad = async (planId, data) => {
  try {
    const { data: row, error } = await supabase
      .from('pagos_planes_parcialidades')
      .insert({
        plan_id: planId,
        orden: data.orden || 0,
        nombre: data.nombre,
        monto: Number(data.monto),
        fecha_limite: data.fecha_limite || null,
        concepto_id: data.concepto_id || null,
      })
      .select()
      .single()
    if (error) throw error
    return { data: row, error: null }
  } catch (error) {
    console.error('[addParcialidad]', error)
    return { data: null, error }
  }
}

export const updateParcialidad = async (id, updates) => {
  try {
    const allowed = ['orden', 'nombre', 'monto', 'fecha_limite', 'concepto_id']
    const safe = {}
    for (const k of allowed) if (k in updates) safe[k] = updates[k]
    const { data, error } = await supabase
      .from('pagos_planes_parcialidades')
      .update(safe)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updateParcialidad]', error)
    return { data: null, error }
  }
}

export const removeParcialidad = async (id) => {
  try {
    const { error } = await supabase
      .from('pagos_planes_parcialidades')
      .delete()
      .eq('id', id)
    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[removeParcialidad]', error)
    return { error }
  }
}

// =====================================================
// ENTREGABLES
// =====================================================

export const getEntregables = async (planId) => {
  try {
    const { data, error } = await supabase
      .from('pagos_planes_entregables')
      .select('*')
      .eq('plan_id', planId)
      .order('created_at', { ascending: true })
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getEntregables]', error)
    return { data: [], error }
  }
}

export const addEntregable = async (planId, data) => {
  try {
    const { data: row, error } = await supabase
      .from('pagos_planes_entregables')
      .insert({
        plan_id: planId,
        nombre: data.nombre,
        regla_tipo: data.regla_tipo,
        hasta_orden: data.regla_tipo === 'parcialidad' ? (data.hasta_orden ?? null) : null,
        // concepto_id se conserva como metadato (qué uniforme se entrega), aunque la
        // regla sea 'parcialidad' (entrega al llegar al Pago N). El esquema lo permite.
        concepto_id: data.concepto_id || null,
      })
      .select()
      .single()
    if (error) throw error
    return { data: row, error: null }
  } catch (error) {
    console.error('[addEntregable]', error)
    return { data: null, error }
  }
}

export const updateEntregable = async (id, updates) => {
  try {
    const allowed = ['nombre', 'regla_tipo', 'hasta_orden', 'concepto_id']
    const safe = {}
    for (const k of allowed) if (k in updates) safe[k] = updates[k]
    const { data, error } = await supabase
      .from('pagos_planes_entregables')
      .update(safe)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updateEntregable]', error)
    return { data: null, error }
  }
}

export const removeEntregable = async (id) => {
  try {
    const { error } = await supabase
      .from('pagos_planes_entregables')
      .delete()
      .eq('id', id)
    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[removeEntregable]', error)
    return { error }
  }
}

export const getEstadoEntregables = async (jugadorId, planId) => {
  try {
    const { data: entregables, error: eErr } = await supabase
      .from('pagos_planes_entregables')
      .select('*')
      .eq('plan_id', planId)
    if (eErr) throw eErr
    if (!entregables?.length) return { data: [], error: null }

    const { data: parcialidades, error: pErr } = await supabase
      .from('pagos_planes_parcialidades')
      .select('*')
      .eq('plan_id', planId)
      .order('orden', { ascending: true })
    if (pErr) throw pErr

    const { data: cargos, error: cErr } = await supabase
      .from('pagos_cargos')
      // monto_final es imprescindible: el umbral del entregable se mide con lo que
      // ESTE jugador debe por sus pagos 1..N, no con el precio de lista del plan.
      .select('parcialidad_id, saldo, estatus, monto_pagado, monto_final')
      .eq('jugador_id', jugadorId)
      .eq('plan_id', planId)
      .neq('estatus', 'cancelado')
    if (cErr) throw cErr

    const pagadoTotal = (cargos || []).reduce((s, c) => s + Number(c.monto_pagado || 0), 0)
    const cargosPorParcialidadId = {}
    ;(cargos || []).forEach((c) => { if (c.parcialidad_id) cargosPorParcialidadId[c.parcialidad_id] = c })

    const data = (entregables || []).map((ent) =>
      computeEntregableEstatus(ent, { parcialidades: parcialidades || [], pagadoTotal, cargosPorParcialidadId })
    )
    return { data, error: null }
  } catch (error) {
    console.error('[getEstadoEntregables]', error)
    return { data: [], error }
  }
}

// =====================================================
// PROMOCIONES
// =====================================================

export const getPromociones = async ({ temporadaId = null, orgId = null } = {}) => {
  try {
    let query = supabase
      .from('pagos_promociones')
      // El conteo de asignados reemplaza a la etiqueta de "alcance": ahora lo
      // que define a una promocion no es a que tipo de cosa aplica, sino a
      // cuanta gente se le otorgo.
      .select('*, pagos_promociones_jugadores(count)')
      .eq('activo', true)
      .order('tipo')
      .order('nombre')

    query = applyOrgFilter(query, orgId)
    if (temporadaId) query = query.eq('temporada_id', temporadaId)

    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getPromociones]', error)
    return { data: [], error }
  }
}

export const createPromocion = async (promoData) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const { data, error } = await supabase
      .from('pagos_promociones')
      .insert({
        org_id: orgId,
        temporada_id: promoData.temporada_id || null,
        nombre: promoData.nombre,
        tipo: promoData.tipo || 'otro',
        // Sin scope: toda promocion aplica solo a quien se le asigne, y eso vive
        // en pagos_promociones_jugadores. Las columnas scope/scope_id quedaron
        // obsoletas (ver 20260819b).
        porcentaje: promoData.porcentaje || null,
        monto_fijo: promoData.monto_fijo || null,
        config: promoData.config || {},
        descripcion: promoData.descripcion || null,
      })
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[createPromocion]', error)
    return { data: null, error }
  }
}

// Solo escribe la promocion. Recalcular los cargos de quien la tiene es del
// motor, y lo dispara actualizarPromocion (promocionAsignacion.js) -- si esto
// lo hiciera aqui, pagos.js tendria que importar al motor, que importa a
// pagos.js.
export const updatePromocion = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('pagos_promociones')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updatePromocion]', error)
    return { data: null, error }
  }
}

// Jugadores asignados a una promoción
export const getPromocionJugadores = async (promocionId) => {
  try {
    const { data, error } = await supabase
      .from('pagos_promociones_jugadores')
      .select('jugador_id, jugadores:jugador_id(id, nombre_completo, numero)')
      .eq('promocion_id', promocionId)
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    return { data: [], error }
  }
}

// Obtener promociones aplicables a un jugador (para asignar desde estado de cuenta)
export const getPromocionesAplicables = async (jugadorId, temporadaId = null) => {
  try {
    const orgId = getActiveOrgOverride() || (await getCurrentStaff())?.org_id || DEFAULT_ORG_ID

    // Obtener cargos del jugador para saber qué planes/conceptos tiene
    let cargosQuery = supabase
      .from('pagos_cargos')
      .select('plan_id, concepto_id')
      .eq('jugador_id', jugadorId)
      .neq('estatus', 'cancelado')
    if (temporadaId) cargosQuery = cargosQuery.eq('temporada_id', temporadaId)
    const { data: cargos } = await cargosQuery

    const planIds = [...new Set((cargos || []).map((c) => c.plan_id).filter(Boolean))]
    const conceptoIds = [...new Set((cargos || []).map((c) => c.concepto_id).filter(Boolean))]

    // Obtener todas las promociones activas
    let promoQuery = supabase
      .from('pagos_promociones')
      .select('*, pagos_promociones_jugadores(jugador_id)')
      .eq('org_id', orgId)
      .eq('activo', true)
    if (temporadaId) {
      promoQuery = promoQuery.or(`temporada_id.eq.${temporadaId},temporada_id.is.null`)
    }
    const { data: promos } = await promoQuery

    // Filtrar: solo promociones que apliquen a los planes/conceptos que tiene el jugador
    const aplicables = (promos || []).filter((p) => {
      // Si tiene plan_id, verificar que el jugador tenga cargos de ese plan
      if (p.plan_id && !planIds.includes(p.plan_id)) return false
      // Si tiene concepto_id, verificar que el jugador tenga cargos con ese concepto
      if (p.concepto_id && !conceptoIds.includes(p.concepto_id)) return false
      // Ya asignada: no hay nada que ofrecerle a este jugador. Antes esto solo
      // se revisaba para scope='jugador'; ahora todas las promociones se
      // asignan, asi que aplica a todas.
      const yaAsignado = (p.pagos_promociones_jugadores || []).some((pj) => pj.jugador_id === jugadorId)
      if (yaAsignado) return false
      return true
    })

    return { data: aplicables, error: null }
  } catch (error) {
    console.error('[getPromocionesAplicables]', error)
    return { data: [], error }
  }
}

// Borrado logico. Devolver los cargos a su precio de lista ya NO se hace aqui:
// lo hace el motor, que corre despues de desactivarla (ver desactivarPromocion en
// promocionAsignacion.js). Antes esta funcion recorria los cargos a mano y solo
// alcanzaba a los que tuvieran promocion_id puesto.
export const deletePromocion = async (id) => {
  try {
    const { error } = await supabase
      .from('pagos_promociones')
      .update({ activo: false })
      .eq('id', id)
    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[deletePromocion]', error)
    return { error }
  }
}

// =====================================================
// PRESUPUESTO DE TEMPORADA
// =====================================================

export const getPresupuesto = async (temporadaId) => {
  try {
    const { data, error } = await supabase
      .from('pagos_presupuesto')
      .select('*, pagos_conceptos:concepto_id(id, nombre, tipo)')
      .eq('temporada_id', temporadaId)
      .order('orden')

    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getPresupuesto]', error)
    return { data: [], error }
  }
}

export const savePresupuestoItem = async ({ temporadaId, conceptoId, monto, descripcion, orden }) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const { data, error } = await supabase
      .from('pagos_presupuesto')
      .upsert({
        org_id: orgId,
        temporada_id: temporadaId,
        concepto_id: conceptoId,
        monto: Number(monto),
        descripcion: descripcion || null,
        orden: orden || 0,
      }, { onConflict: 'org_id,temporada_id,concepto_id' })
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[savePresupuestoItem]', error)
    return { data: null, error }
  }
}

export const deletePresupuestoItem = async (id) => {
  try {
    const { error } = await supabase
      .from('pagos_presupuesto')
      .delete()
      .eq('id', id)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[deletePresupuestoItem]', error)
    return { error }
  }
}

// =====================================================
// ASIGNACION DE PLAN POR JUGADOR
// =====================================================

export const getJugadorPlanActivo = async (jugadorId, temporadaId) => {
  try {
    const { data, error } = await supabase
      .from('pagos_jugador_plan')
      .select('*, pagos_planes:plan_id(id, nombre, scope)')
      .eq('jugador_id', jugadorId)
      .eq('temporada_id', temporadaId)
      .eq('activo', true)
      .maybeSingle()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[getJugadorPlanActivo]', error)
    return { data: null, error }
  }
}

export const getAsignacionesPorPlan = async (planId) => {
  try {
    const { data, error } = await supabase
      .from('pagos_jugador_plan')
      .select('*, jugadores:jugador_id(id, nombre_completo, numero)')
      .eq('plan_id', planId)
      .eq('activo', true)
      .order('created_at')

    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getAsignacionesPorPlan]', error)
    return { data: [], error }
  }
}

export const getTodasAsignaciones = async (temporadaId = null, orgId = null) => {
  try {
    let query = supabase
      .from('pagos_jugador_plan')
      .select(`
        *,
        jugadores:jugador_id(id, nombre_completo, numero, imagen_path, imagen_url, familia_id,
          familias:familia_id(id, nombre)
        ),
        pagos_planes:plan_id(id, nombre, scope)
      `)
      .eq('activo', true)
      .order('created_at', { ascending: false })

    query = applyOrgFilter(query, orgId)
    if (temporadaId) query = query.eq('temporada_id', temporadaId)

    const { data, error } = await query
    if (error) throw error
    await signImagesDeep(data)
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getTodasAsignaciones]', error)
    return { data: [], error }
  }
}

export const desasignarPlan = async (asignacionId) => {
  try {
    // Obtener datos de la asignacion
    const { data: asignacion } = await supabase
      .from('pagos_jugador_plan')
      .select('jugador_id, plan_id, temporada_id')
      .eq('id', asignacionId)
      .single()

    if (!asignacion) throw new Error('Asignacion no encontrada')

    // Desactivar asignacion
    await supabase
      .from('pagos_jugador_plan')
      .update({ activo: false, desactivado_at: new Date().toISOString() })
      .eq('id', asignacionId)

    // Cancelar cargos no pagados de este plan
    await supabase
      .from('pagos_cargos')
      .update({ estatus: 'cancelado', notas: 'Plan desasignado' })
      .eq('jugador_id', asignacion.jugador_id)
      .eq('plan_id', asignacion.plan_id)
      .eq('temporada_id', asignacion.temporada_id)
      .in('estatus', ['pendiente', 'vencido'])

    return { error: null }
  } catch (error) {
    console.error('[desasignarPlan]', error)
    return { error }
  }
}

/**
 * Reasignar plan a un jugador. Lo que REALMENTE hace, si ya tiene otro plan
 * activo en la misma temporada:
 *   1. Cancela los cargos 'pendiente' y 'vencido' de OTROS planes que compartan
 *      concepto con el nuevo (lo pagado no se toca)
 *   2. Crea la asignacion nueva, salvo que ya exista la misma (ahi solo completa
 *      las parcialidades que le falten)
 *   3. Genera los cargos que falten
 *
 * PENDIENTE CONOCIDO: NO desactiva la asignacion anterior (el docstring viejo
 * decia que si). La fila vieja de pagos_jugador_plan queda activa pero sin
 * cargos vivos, asi que el jugador aparece con dos planes en el listado de
 * asignaciones. Es preexistente y quedo fuera del alcance del rediseno de
 * asignacion; el calculo del dinero no se toca.
 */
const reasignarPlanJugador = async (jugadorId, planId, temporadaId, orgId, promociones, jugadorFamilia, familiaCount, jugadorPosicion = {}, { soloFaltantes = false } = {}) => {
  const { data: staff } = (await getCurrentStaff()) || {}

  // Verificar si ya tiene este mismo plan asignado
  const { data: yaAsignado } = await supabase
    .from('pagos_jugador_plan')
    .select('id')
    .eq('jugador_id', jugadorId)
    .eq('plan_id', planId)
    .eq('activo', true)
    .maybeSingle()

  // Si ya tiene el plan, no crear nueva asignacion pero SÍ generar cargos faltantes
  const skipAsignacion = !!yaAsignado

  // Obtener conceptos del nuevo plan para detectar conflictos
  const { data: newParcs } = await supabase
    .from('pagos_planes_parcialidades')
    .select('concepto_id')
    .eq('plan_id', planId)
  const newConceptoIds = (newParcs || []).map((p) => p.concepto_id).filter(Boolean)

  // Si hay conceptos que ya estan en cargos activos de otro plan, cancelar esos cargos.
  // Al SOLO completar faltantes no se hace: el club no esta reasignando nada, y
  // tumbar cargos de otro plan seria un efecto que nadie pidio.
  if (newConceptoIds.length > 0 && !soloFaltantes) {
    await supabase
      .from('pagos_cargos')
      .update({ estatus: 'cancelado', notas: 'Reemplazado por nuevo plan' })
      .eq('jugador_id', jugadorId)
      .eq('temporada_id', temporadaId)
      .in('concepto_id', newConceptoIds)
      .in('estatus', ['pendiente', 'vencido'])
      .neq('plan_id', planId)
  }

  // Crear asignacion (solo si no existe)
  if (!skipAsignacion) {
    await supabase
      .from('pagos_jugador_plan')
      .insert({
        org_id: orgId,
        jugador_id: jugadorId,
        temporada_id: temporadaId,
        plan_id: planId,
        asignado_por: staff?.id,
      })
  }

  // Obtener parcialidades del plan
  const { data: planData } = await supabase
    .from('pagos_planes')
    .select('*, pagos_planes_parcialidades(*)')
    .eq('id', planId)
    .single()

  const parcialidades = (planData?.pagos_planes_parcialidades || []).sort((a, b) => a.orden - b.orden)
  if (!parcialidades.length) return []

  // Verificar qué parcialidades ya tienen cargo activo para este jugador
  const { data: cargosExistentes } = await supabase
    .from('pagos_cargos')
    .select('parcialidad_id, estatus')
    .eq('jugador_id', jugadorId)
    .eq('plan_id', planId)
    .eq('temporada_id', temporadaId)

  // Al reasignar, un cargo cancelado se vuelve a emitir (es una decision deliberada
  // de re-aplicar el plan). Al solo completar faltantes NO: cancelar fue una
  // decision del club y completar el plan no debe deshacerla.
  const parcConCargo = new Set((cargosExistentes || [])
    .filter((c) => soloFaltantes || c.estatus !== 'cancelado')
    .map((c) => c.parcialidad_id))
  const parcSinCargo = parcialidades.filter((p) => !parcConCargo.has(p.id))

  if (!parcSinCargo.length) return [] // todas ya tienen cargo

  // Calcular descuento para este jugador POR CADA PARCIALIDAD
  // (porque una promo puede aplicar solo a ciertos conceptos; usa parc.concepto_id)
  // Generar cargos solo para parcialidades sin cargo existente
  const cargos = parcSinCargo.map((parc) => {
    const { descuento, promocionId } = calcularDescuento({
      promociones, jugadorId, jugadorFamilia, jugadorPosicion, parc, planId,
    })
    const montoOriginal = Number(parc.monto)
    const descuentoMonto = montoOriginal * (descuento / 100)
    const montoFinal = montoOriginal - descuentoMonto

    return {
      org_id: orgId,
      temporada_id: temporadaId,
      jugador_id: jugadorId,
      plan_id: planId,
      parcialidad_id: parc.id,
      concepto_id: parc.concepto_id || null,
      monto_original: montoOriginal,
      descuento_porcentaje: descuento,
      descuento_monto: descuentoMonto,
      monto_final: montoFinal,
      monto_pagado: 0,
      saldo: montoFinal,
      fecha_vencimiento: parc.fecha_limite,
      estatus: 'pendiente',
      promocion_id: promocionId,
    }
  })

  const { data: inserted } = await supabase
    .from('pagos_cargos')
    .insert(cargos)
    .select()

  return inserted || []
}

// =====================================================
// APLICAR PLAN → genera cargos con asignacion tracking
// =====================================================

/**
 * Genera los cargos de un plan para una lista EXPLICITA de jugadores.
 *
 * Antes se llamaba aplicarPlan(planId) y resolvia el universo por su cuenta desde
 * scope + scope_id: el admin apretaba un boton y descubria a quien se le cobro
 * despues. Ahora la pantalla decide a quien y esta funcion solo cobra.
 *
 * Idempotente: reasignarPlanJugador no duplica la asignacion y solo genera los
 * cargos de las parcialidades que aun no lo tienen (util para rellenar huecos
 * cuando entra alguien nuevo a un grupo).
 */
export const asignarPlan = async ({ planId, jugadorIds = [], soloFaltantes = false }) => {
  try {
    if (!jugadorIds.length) {
      throw new Error('Selecciona al menos un jugador, grupo o familia para asignar el plan.')
    }
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    // Obtener plan con items
    const { data: plan, error: planError } = await supabase
      .from('pagos_planes')
      .select('*, pagos_planes_items(*)')
      .eq('id', planId)
      .single()

    if (planError) throw planError

    // Cargar parcialidades del plan
    const { data: parcialidades, error: parcError } = await supabase
      .from('pagos_planes_parcialidades')
      .select('*')
      .eq('plan_id', planId)
      .order('orden', { ascending: true })
    if (parcError) throw parcError
    if (!parcialidades?.length) throw new Error('El plan no tiene parcialidades')

    // Validar que las parcialidades cuadren con el total de conceptos
    const totalConceptos = (plan.pagos_planes_items || []).reduce((s, it) => s + Number(it.monto || 0), 0)
    const totalParc = parcialidades.reduce((s, p) => s + Number(p.monto || 0), 0)
    if (Math.round((totalParc - totalConceptos) * 100) !== 0) {
      const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
      const dif = totalParc - totalConceptos
      throw new Error(
        `Las parcialidades (${fmt(totalParc)}) no suman el total de conceptos (${fmt(totalConceptos)}); ` +
        `${dif > 0 ? 'sobran' : 'faltan'} ${fmt(Math.abs(dif))}. ` +
        'Si el plan incluye uniformes, revisa que no tenga prendas duplicadas o inconsistentes.'
      )
    }

    // La temporada debe estar abierta para cobrar (el aislamiento por deporte
    // ya paso: quien llega aqui viene resuelto por resolverJugadores, que acota
    // los grupos al deporte de la temporada antes de armar jugadorIds).
    const { data: temporada, error: tempError } = await supabase
      .from('temporadas')
      .select('deporte_id, estado')
      .eq('id', plan.temporada_id)
      .single()
    if (tempError) throw tempError
    if (temporada.estado !== 'abierta') {
      throw new Error('La temporada debe estar abierta para aplicar un plan')
    }

    // El padron manda: solo se le cobra a quien participa en la temporada del plan.
    const inscritos = new Set(await getJugadoresInscritos(plan.temporada_id))
    const solicitados = [...new Set(jugadorIds)]
    const noInscritos = solicitados.filter((id) => !inscritos.has(id))
    jugadorIds = solicitados.filter((id) => inscritos.has(id))

    if (!jugadorIds.length) {
      throw new Error(
        'Ninguno de los seleccionados esta inscrito en la temporada de este plan. ' +
        'Inscribelos en Administracion → Inscripciones y vuelve a intentarlo.'
      )
    }

    // Obtener promociones activas con jugadores asignados
    const { data: promocionesRaw } = await supabase
      .from('pagos_promociones')
      .select('*, pagos_promociones_jugadores(jugador_id)')
      .eq('org_id', orgId)
      .eq('activo', true)
      .or(plan.temporada_id
        ? `temporada_id.eq.${plan.temporada_id},temporada_id.is.null`
        : 'temporada_id.is.null'
      )

    // Enriquecer con lista de jugador_ids para scope='jugador'
    const promociones = (promocionesRaw || []).map((p) => ({
      ...p,
      _jugadorIds: (p.pagos_promociones_jugadores || []).map((pj) => pj.jugador_id),
    }))

    // Obtener info de familia de cada jugador
    const { data: jugadoresInfo } = await supabase
      .from('jugadores')
      .select('id, familia_id, fecha_nacimiento, created_at')
      .in('id', jugadorIds)

    // Contar TODOS los hijos por familia (no solo los del plan)
    const familiaIds = [...new Set((jugadoresInfo || []).map((j) => j.familia_id).filter(Boolean))]
    const familiaCountTotal = {}
    const jugadorFamilia = {}

    const jugadorPosicion = {}
    if (familiaIds.length > 0) {
      const { data: allFamilyKids } = await supabase
        .from('jugadores')
        .select('id, familia_id, fecha_nacimiento, created_at')
        .in('familia_id', familiaIds)
        .eq('activo', true)
        .or('deleted.is.null,deleted.eq.false')
        .order('fecha_nacimiento', { ascending: true })

      // Contar hijos totales por familia
      const familyMembers = {};
      (allFamilyKids || []).forEach((j) => {
        if (!j.familia_id) return
        familiaCountTotal[j.familia_id] = (familiaCountTotal[j.familia_id] || 0) + 1
        if (!familyMembers[j.familia_id]) familyMembers[j.familia_id] = []
        familyMembers[j.familia_id].push(j.id)
      })

      // Asignar posición de cada jugador en su familia (1ro, 2do, 3ro...)
      // Ordenados por fecha de nacimiento (mayor = primero registrado = hijo_1 sin descuento)
      ;(jugadoresInfo || []).forEach((j) => {
        if (j.familia_id) {
          jugadorFamilia[j.id] = j.familia_id
        }
      })

      // jugadorPosicion: { jugadorId: posición (1-based) }
      Object.entries(familyMembers).forEach(([famId, memberIds]) => {
        memberIds.forEach((mId, idx) => {
          jugadorPosicion[mId] = idx + 1 // 1 = primer hijo (sin descuento), 2 = segundo, etc.
        })
      })
    } else {
      ;(jugadoresInfo || []).forEach((j) => {
        if (j.familia_id) jugadorFamilia[j.id] = j.familia_id
      })
    }

    // Reasignar plan a cada jugador (cancela anterior si existe)
    let totalCargos = 0
    for (const jugadorId of jugadorIds) {
      const cargos = await reasignarPlanJugador(
        jugadorId, planId, plan.temporada_id, orgId,
        promociones, jugadorFamilia, familiaCountTotal, jugadorPosicion, { soloFaltantes }
      )
      totalCargos += cargos.length
    }

    return { data: { asignados: jugadorIds.length, noInscritos }, error: null, count: totalCargos }
  } catch (error) {
    console.error('[asignarPlan]', error)
    return { data: null, error }
  }
}

export const getCobranzaPendiente = async (temporadaId = null, orgId = null) => {
  try {
    await actualizarVencidos()

    let query = supabase
      .from('pagos_cargos')
      .select(`
        jugador_id,
        monto_final,
        monto_pagado,
        saldo,
        estatus,
        jugadores:jugador_id(id, nombre_completo, numero, imagen_path, imagen_url, familia_id,
          familias:familia_id(id, nombre)
        )
      `)
      .neq('estatus', 'cancelado')
      .neq('estatus', 'pagado')

    query = applyOrgFilter(query, orgId)
    if (temporadaId) query = query.eq('temporada_id', temporadaId)

    const { data, error } = await query
    if (error) throw error
    await signImagesDeep(data)

    // Agrupar por jugador
    const byJugador = {}
    ;(data || []).forEach((c) => {
      const jId = c.jugador_id
      if (!byJugador[jId]) {
        byJugador[jId] = {
          jugador: c.jugadores,
          familia: c.jugadores?.familias,
          saldo: 0,
          vencido: 0,
          total_cargos: 0,
          pagados: 0,
        }
      }
      byJugador[jId].saldo += Number(c.saldo || 0)
      byJugador[jId].total_cargos++
      if (c.estatus === 'vencido') byJugador[jId].vencido += Number(c.saldo || 0)
    })

    // Convertir a array y ordenar por vencido desc, saldo desc
    const jugadores = Object.values(byJugador)
      .filter((j) => j.saldo > 0)
      .sort((a, b) => b.vencido - a.vencido || b.saldo - a.saldo)

    // Agrupar por familia
    const byFamilia = {}
    jugadores.forEach((j) => {
      const famId = j.familia?.id || `sin_${j.jugador?.id}`
      const famNombre = j.familia?.nombre || j.jugador?.nombre_completo || 'Sin familia'
      if (!byFamilia[famId]) {
        byFamilia[famId] = { nombre: famNombre, familia: j.familia, saldo: 0, vencido: 0, jugadores: [] }
      }
      byFamilia[famId].saldo += j.saldo
      byFamilia[famId].vencido += j.vencido
      byFamilia[famId].jugadores.push(j)
    })

    const familias = Object.values(byFamilia)
      .sort((a, b) => b.vencido - a.vencido || b.saldo - a.saldo)

    return { data: { jugadores, familias }, error: null }
  } catch (error) {
    console.error('[getCobranzaPendiente]', error)
    return { data: { jugadores: [], familias: [] }, error }
  }
}

// =====================================================
// CARGOS
// =====================================================

export const getCargosByJugador = async (jugadorId, temporadaId = null) => {
  await actualizarVencidos()
  try {
    let query = supabase
      .from('pagos_cargos')
      .select(`
        *,
        pagos_conceptos:concepto_id(id, nombre, tipo),
        pagos_planes_parcialidades:parcialidad_id(id, nombre),
        pagos_promociones:promocion_id(id, nombre, tipo),
        pagos_planes:plan_id(id, nombre),
        pagos_transacciones_detalle(
          id, monto_aplicado,
          pagos_transacciones:transaccion_id(id, fecha_pago, metodo_pago, referencia, anulado)
        )
      `)
      .eq('jugador_id', jugadorId)
      .neq('estatus', 'cancelado')
      .order('fecha_vencimiento')

    if (temporadaId) query = query.eq('temporada_id', temporadaId)

    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getCargosByJugador]', error)
    return { data: [], error }
  }
}

export const getCargosByFamilia = async (familiaId, temporadaId = null) => {
  try {
    // Obtener jugadores de la familia
    const { data: jugadores } = await supabase
      .from('jugadores')
      .select('id, nombre_completo, numero, imagen_path, imagen_url')
      .eq('familia_id', familiaId)
      .or('deleted.is.null,deleted.eq.false')

    await signImagesDeep(jugadores)

    if (!jugadores?.length) return { data: [], error: null }

    const jugadorIds = jugadores.map((j) => j.id)

    let query = supabase
      .from('pagos_cargos')
      .select(`
        *,
        pagos_conceptos:concepto_id(id, nombre, tipo),
        pagos_planes_parcialidades:parcialidad_id(id, nombre),
        pagos_promociones:promocion_id(id, nombre, tipo),
        pagos_planes:plan_id(id, nombre),
        jugadores:jugador_id(id, nombre_completo, numero),
        pagos_transacciones_detalle(
          id, monto_aplicado,
          pagos_transacciones:transaccion_id(id, fecha_pago, metodo_pago, referencia, anulado)
        )
      `)
      .in('jugador_id', jugadorIds)
      .neq('estatus', 'cancelado')
      .order('jugador_id')
      .order('fecha_vencimiento')

    if (temporadaId) query = query.eq('temporada_id', temporadaId)

    const { data, error } = await query
    if (error) throw error
    return { data: data || [], jugadores, error: null }
  } catch (error) {
    console.error('[getCargosByFamilia]', error)
    return { data: [], error }
  }
}

export const cancelarCargo = async (cargoId, motivo = '') => {
  try {
    const { data, error } = await supabase
      .from('pagos_cargos')
      .update({ estatus: 'cancelado', notas: motivo })
      .eq('id', cargoId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[cancelarCargo]', error)
    return { data: null, error }
  }
}

export const updateCargo = async (cargoId, updates) => {
  try {
    const allowedFields = ['monto_original', 'monto_final', 'saldo', 'fecha_vencimiento', 'notas']
    const filtered = {}
    allowedFields.forEach((f) => { if (updates[f] !== undefined) filtered[f] = updates[f] })

    // Recalcular saldo si cambia monto_final
    if (filtered.monto_final !== undefined) {
      const { data: current } = await supabase
        .from('pagos_cargos')
        .select('monto_pagado')
        .eq('id', cargoId)
        .single()
      filtered.saldo = Number(filtered.monto_final) - Number(current?.monto_pagado || 0)
      filtered.monto_original = filtered.monto_final
    }

    const { data, error } = await supabase
      .from('pagos_cargos')
      .update(filtered)
      .eq('id', cargoId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updateCargo]', error)
    return { data: null, error }
  }
}

export const crearCargoManual = async ({ jugadorId, conceptoId, monto, fechaVencimiento, temporadaId, notas }) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const { data, error } = await supabase
      .from('pagos_cargos')
      .insert({
        org_id: orgId,
        temporada_id: temporadaId || null,
        jugador_id: jugadorId,
        concepto_id: conceptoId || null,
        monto_original: monto,
        monto_final: monto,
        monto_pagado: 0,
        saldo: monto,
        fecha_vencimiento: fechaVencimiento || null,
        estatus: 'pendiente',
        notas: notas || null,
      })
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[crearCargoManual]', error)
    return { data: null, error }
  }
}

// =====================================================
// TRANSACCIONES (pagos)
// =====================================================

export const getTransacciones = async ({ temporadaId = null, jugadorId = null, orgId = null } = {}) => {
  try {
    let query = supabase
      .from('pagos_transacciones')
      .select(`
        *,
        jugadores:jugador_id(id, nombre_completo, numero, imagen_path, imagen_url),
        registrado_por_staff:registrado_por(id, nombre_completo),
        anulado_por_staff:anulado_por(id, nombre_completo),
        pagos_transacciones_detalle(
          id, monto_aplicado,
          pagos_cargos:cargo_id(id, concepto_id,
            pagos_conceptos:concepto_id(nombre)
          )
        ),
        pagos_saldo_favor!transaccion_origen_id(
          id, monto, monto_usado, saldo_disponible, activo
        )
      `)
      .order('fecha_pago', { ascending: false })
      .order('created_at', { ascending: false })

    query = applyOrgFilter(query, orgId)
    if (jugadorId) query = query.eq('jugador_id', jugadorId)

    // Filtrar por temporada via cargos si hay temporada
    // (no directo, se filtra en frontend o via join)

    const { data, error } = await query
    if (error) throw error
    await signImagesDeep(data)
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getTransacciones]', error)
    return { data: [], error }
  }
}

export const registrarPago = async ({ jugadorId, monto, metodoPago, referencia, comprobantePath, fechaPago, cargosAplicar, notas }) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    // Crear transaccion
    const { data: transaccion, error: txError } = await supabase
      .from('pagos_transacciones')
      .insert({
        org_id: orgId,
        jugador_id: jugadorId,
        monto,
        metodo_pago: metodoPago || 'efectivo',
        referencia: referencia || null,
        comprobante_path: comprobantePath || null,
        fecha_pago: fechaPago || new Date().toISOString().split('T')[0],
        registrado_por: staff?.id,
        notas: notas || null,
      })
      .select()
      .single()

    if (txError) throw txError

    // Aplicar a cargos
    if (cargosAplicar?.length) {
      const detalles = cargosAplicar.map((ca) => ({
        transaccion_id: transaccion.id,
        cargo_id: ca.cargo_id,
        monto_aplicado: ca.monto_aplicado,
      }))

      const { error: detError } = await supabase
        .from('pagos_transacciones_detalle')
        .insert(detalles)

      if (detError) throw detError
    }

    return { data: transaccion, error: null }
  } catch (error) {
    console.error('[registrarPago]', error)
    return { data: null, error }
  }
}

// Retirar un saldo a favor: se anula lo que AUN no se ha usado. Lo ya aplicado no
// se revierte -- ese dinero cerro cargos, y deshacerlo reabriria deudas que la
// familia da por pagadas.
//
// La escritura es la misma que ya hacia quitarPromocion desde 2026-08. Lo que
// faltaba era poder hacerla sobre un saldo QUE NO VIENE DE UNA PROMOCION: el
// sobrante de un pago no cuelga de ninguna, asi que no habia palanca para
// corregirlo. Medido en PROD el 2026-09-01: 24 saldos por $14,760.
export const retirarSaldoFavor = async (saldoFavorId, { motivo = null } = {}) => {
  try {
    const { data: saldo, error: sErr } = await supabase
      .from('pagos_saldo_favor')
      .select('id, monto, monto_usado, saldo_disponible, descripcion, activo')
      .eq('id', saldoFavorId)
      .single()
    if (sErr) throw sErr

    const disponible = Number(saldo?.saldo_disponible || 0)
    const aplicado = Number(saldo?.monto_usado || 0)

    // Idempotente: retirarlo dos veces no es un error, simplemente no hay nada
    // que retirar la segunda vez.
    if (!saldo?.activo || disponible <= 0) {
      return { data: { retirado: 0, aplicado, yaEstaba: true }, error: null }
    }

    const descripcion = motivo
      ? `${saldo.descripcion ? `${saldo.descripcion} — ` : ''}Retirado: ${motivo}`
      : saldo.descripcion

    const { error } = await supabase
      .from('pagos_saldo_favor')
      .update({ saldo_disponible: 0, activo: false, descripcion })
      .eq('id', saldoFavorId)
    if (error) throw error

    return { data: { retirado: disponible, aplicado, yaEstaba: false }, error: null }
  } catch (error) {
    console.error('[retirarSaldoFavor]', error)
    return { data: null, error }
  }
}

export const anularPago = async (transaccionId, motivo = '') => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}

    // Obtener los cargo_ids afectados ANTES de anular (para recalcular saldos)
    const { data: detalles } = await supabase
      .from('pagos_transacciones_detalle')
      .select('cargo_id')
      .eq('transaccion_id', transaccionId)

    const cargoIds = [...new Set((detalles || []).map((d) => d.cargo_id))]

    // Marcar como anulado (no borrar detalles — se conservan como evidencia)
    const { data, error } = await supabase
      .from('pagos_transacciones')
      .update({
        anulado: true,
        anulado_at: new Date().toISOString(),
        anulado_por: staff?.id,
        anulado_motivo: motivo,
      })
      .eq('id', transaccionId)
      .select()
      .single()

    if (error) throw error

    // Recalcular saldo de cada cargo afectado
    // El trigger solo se dispara con INSERT/UPDATE/DELETE en detalle,
    // pero como no borramos detalles, recalculamos manualmente
    for (const cargoId of cargoIds) {
      // Sumar solo pagos de transacciones NO anuladas
      const { data: pagosActivos } = await supabase
        .from('pagos_transacciones_detalle')
        .select('monto_aplicado, pagos_transacciones!inner(anulado)')
        .eq('cargo_id', cargoId)
        .eq('pagos_transacciones.anulado', false)

      const totalPagado = (pagosActivos || []).reduce((sum, d) => sum + Number(d.monto_aplicado || 0), 0)

      const { data: cargo } = await supabase
        .from('pagos_cargos')
        .select('monto_final')
        .eq('id', cargoId)
        .single()

      const montoFinal = Number(cargo?.monto_final || 0)
      const saldo = montoFinal - totalPagado
      let estatus = 'pendiente'
      if (totalPagado >= montoFinal) estatus = 'pagado'
      else if (totalPagado > 0) estatus = 'parcial'

      await supabase
        .from('pagos_cargos')
        .update({ monto_pagado: totalPagado, saldo, estatus })
        .eq('id', cargoId)
    }

    // El sobrante que ESTE pago genero se va con el: si el pago no existio, el
    // credito que sobro tampoco. Antes se quedaba vivo, y era la via por la que
    // se acumulaban saldos que nadie podia corregir.
    const { data: sobrantes } = await supabase
      .from('pagos_saldo_favor')
      .select('id')
      .eq('transaccion_origen_id', transaccionId)
      .eq('activo', true)

    let saldoRetirado = 0
    for (const s of (sobrantes || [])) {
      const { data: r } = await retirarSaldoFavor(s.id, { motivo: 'pago anulado' })
      saldoRetirado = Math.round((saldoRetirado + Number(r?.retirado || 0)) * 100) / 100
    }

    // Y el saldo que ESTE pago consumio se le devuelve: si el pago no existio, el
    // credito nunca se gasto. Sin esto el cargo se reabria y el credito seguia
    // consumido -- la familia perdia por los dos lados, en silencio.
    //
    // La fila de la aplicacion se conserva como evidencia, igual que el detalle de
    // la transaccion: es su transaccion la que dice que esta anulada.
    const { data: aplicaciones } = await supabase
      .from('pagos_saldo_favor_aplicaciones')
      .select('id, saldo_favor_id, monto_aplicado')
      .eq('transaccion_id', transaccionId)

    let saldoDevuelto = 0
    for (const ap of (aplicaciones || [])) {
      const { data: sf } = await supabase
        .from('pagos_saldo_favor')
        .select('id, monto, monto_usado, saldo_disponible, activo')
        .eq('id', ap.saldo_favor_id)
        .single()
      if (!sf) continue

      const monto = Number(ap.monto_aplicado || 0)
      const usado = Math.max(0, Math.round((Number(sf.monto_usado || 0) - monto) * 100) / 100)

      // Un saldo INACTIVO lo cerro el club a proposito: agotarlo nunca lo
      // desactiva, solo retirarlo lo hace. Deshacer el pago le corrige la cuenta
      // -- lo aplicado deja de estarlo -- pero no le devuelve el dinero, porque
      // eso seria resucitar un credito que alguien decidio quitar.
      const cambios = sf.activo
        ? {
          monto_usado: usado,
          saldo_disponible: Math.round((Number(sf.saldo_disponible || 0) + monto) * 100) / 100,
        }
        : { monto_usado: usado }

      const { error: dErr } = await supabase
        .from('pagos_saldo_favor')
        .update(cambios)
        .eq('id', sf.id)
      if (dErr) throw dErr
      if (sf.activo) saldoDevuelto = Math.round((saldoDevuelto + monto) * 100) / 100
    }

    return { data: { ...data, saldoRetirado, saldoDevuelto }, error: null }
  } catch (error) {
    console.error('[anularPago]', error)
    return { data: null, error }
  }
}

// =====================================================
// DASHBOARD / ESTADÍSTICAS
// =====================================================

// Actualizar cargos vencidos (llamar antes de consultar)
const actualizarVencidos = async () => {
  try {
    const orgId = getActiveOrgOverride() || null
    await supabase.rpc('actualizar_cargos_vencidos', { p_org_id: orgId })
  } catch (e) {
    // silently fail — non-critical
  }
}

export const getDashboardStats = async (temporadaId = null, orgId = null) => {
  try {
    await actualizarVencidos()

    let query = supabase
      .from('pagos_cargos')
      .select('monto_original, descuento_monto, monto_final, monto_pagado, saldo, estatus')
      .neq('estatus', 'cancelado')

    query = applyOrgFilter(query, orgId)
    if (temporadaId) query = query.eq('temporada_id', temporadaId)

    const { data, error } = await query
    if (error) throw error

    const stats = {
      // total_cargos es el NETO (lo que de verdad se espera cobrar). Junto a el
      // van el precio de lista y lo descontado, que es la lectura que pedia el
      // club: cuanto esperabamos y cuanto se fue en descuentos.
      total_lista: 0,
      total_descuentos: 0,
      total_cargos: 0,
      total_cobrado: 0,
      total_pendiente: 0,
      total_vencido: 0,
      num_cargos: (data || []).length,
      num_pagados: 0,
      num_pendientes: 0,
      num_vencidos: 0,
      num_parciales: 0,
    }

    ;(data || []).forEach((c) => {
      stats.total_lista += Number(c.monto_original || c.monto_final || 0)
      stats.total_descuentos += Number(c.descuento_monto || 0)
      stats.total_cargos += Number(c.monto_final)
      stats.total_cobrado += Number(c.monto_pagado)
      if (c.estatus === 'pagado') stats.num_pagados++
      else if (c.estatus === 'vencido') { stats.num_vencidos++; stats.total_vencido += Number(c.saldo) }
      else if (c.estatus === 'parcial') { stats.num_parciales++; stats.total_pendiente += Number(c.saldo) }
      else if (c.estatus === 'pendiente') { stats.num_pendientes++; stats.total_pendiente += Number(c.saldo) }
    })

    return { data: stats, error: null }
  } catch (error) {
    console.error('[getDashboardStats]', error)
    return { data: null, error }
  }
}

// =====================================================
// ESTADO DE CUENTA PUBLICO (via QR+PIN)
// =====================================================

export const getEstadoCuentaPublico = async (accessToken, pin) => {
  try {
    const { data, error } = await supabase.rpc('get_estado_cuenta_publico', {
      p_access_token: accessToken,
      p_pin: pin,
    })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[getEstadoCuentaPublico]', error)
    return { data: null, error }
  }
}

// =====================================================
// COMPROBANTES (Storage)
// =====================================================

const COMPROBANTES_BUCKET = 'pagos-comprobantes'

export const uploadComprobante = async (orgId, file) => {
  try {
    const ext = file.name.split('.').pop() || 'pdf'
    const timestamp = Date.now()
    const path = `org/${orgId}/${timestamp}.${ext}`

    const { error: uploadError } = await supabase.storage
      .from(COMPROBANTES_BUCKET)
      .upload(path, file, { contentType: file.type })

    if (uploadError) throw uploadError
    return { path, error: null }
  } catch (error) {
    console.error('[uploadComprobante]', error)
    return { path: null, error }
  }
}

export const getComprobanteUrl = async (filePath, { downloadFilename } = {}) => {
  if (!filePath) return null
  const opts = downloadFilename ? { download: downloadFilename } : {}
  const { data, error } = await supabase.storage
    .from(COMPROBANTES_BUCKET)
    .createSignedUrl(filePath, 300, opts)
  if (error) {
    console.error('[getComprobanteUrl]', error)
    return null
  }
  return data?.signedUrl || null
}

// =====================================================
// SALDO A FAVOR
// =====================================================

export const getSaldoFavor = async (jugadorId, { soloDisponible = true } = {}) => {
  try {
    let query = supabase
      .from('pagos_saldo_favor')
      .select(`
        *,
        pagos_saldo_favor_aplicaciones(
          id, monto_aplicado, created_at, transaccion_id,
          pagos_cargos:cargo_id(id, concepto_id,
            pagos_conceptos:concepto_id(nombre)
          )
        )
      `)
      .eq('jugador_id', jugadorId)
      .eq('activo', true)
      .order('created_at', { ascending: false })

    if (soloDisponible) query = query.gt('saldo_disponible', 0)

    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getSaldoFavor]', error)
    return { data: [], error }
  }
}

export const getSaldoFavorTotal = async (jugadorId) => {
  try {
    const { data, error } = await supabase
      .from('pagos_saldo_favor')
      .select('saldo_disponible')
      .eq('jugador_id', jugadorId)
      .eq('activo', true)
      .gt('saldo_disponible', 0)

    if (error) throw error
    const total = (data || []).reduce((sum, s) => sum + Number(s.saldo_disponible || 0), 0)
    return { total, error: null }
  } catch (error) {
    return { total: 0, error }
  }
}

export const crearSaldoFavor = async ({ jugadorId, monto, origen = 'pago_sin_concepto', descripcion = null, transaccionOrigenId = null, cargoOrigenId = null, temporadaId = null, promocionId = null }) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    // Obtener familia_id del jugador
    const { data: jugador } = await supabase
      .from('jugadores')
      .select('familia_id')
      .eq('id', jugadorId)
      .single()

    const { data, error } = await supabase
      .from('pagos_saldo_favor')
      .insert({
        org_id: orgId,
        jugador_id: jugadorId,
        familia_id: jugador?.familia_id || null,
        monto,
        monto_usado: 0,
        saldo_disponible: monto,
        origen,
        descripcion,
        transaccion_origen_id: transaccionOrigenId,
        cargo_origen_id: cargoOrigenId,
        promocion_id: promocionId,
        temporada_id: temporadaId,
        created_by: staff?.id,
      })
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[crearSaldoFavor]', error)
    return { data: null, error }
  }
}

export const aplicarSaldoFavorACargo = async (saldoFavorId, cargoId, montoAplicar) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    // Obtener saldo a favor
    const { data: saldo, error: sError } = await supabase
      .from('pagos_saldo_favor')
      .select('*')
      .eq('id', saldoFavorId)
      .single()

    if (sError) throw sError
    if (Number(saldo.saldo_disponible) < montoAplicar) {
      throw new Error('Saldo a favor insuficiente')
    }

    // Obtener cargo
    const { data: cargo, error: cError } = await supabase
      .from('pagos_cargos')
      .select('*')
      .eq('id', cargoId)
      .single()

    if (cError) throw cError

    const montoReal = Math.min(montoAplicar, Number(cargo.saldo))

    // 1. Crear transacción (para que aparezca en pantalla de pagos)
    const { data: transaccion, error: txError } = await supabase
      .from('pagos_transacciones')
      .insert({
        org_id: orgId,
        jugador_id: cargo.jugador_id,
        monto: montoReal,
        metodo_pago: 'saldo_favor',
        referencia: null,
        fecha_pago: new Date().toISOString().split('T')[0],
        registrado_por: staff?.id,
        notas: 'Aplicación de saldo a favor',
      })
      .select()
      .single()

    if (txError) throw txError

    // 2. Crear detalle de transacción → cargo
    await supabase
      .from('pagos_transacciones_detalle')
      .insert({
        transaccion_id: transaccion.id,
        cargo_id: cargoId,
        monto_aplicado: montoReal,
      })

    // 3. Registrar la aplicación en tabla de saldo favor.
    //    `transaccion_id` es lo que permite DESHACERLO: sin el, anular este pago
    //    reabria el cargo y dejaba el credito consumido.
    await supabase
      .from('pagos_saldo_favor_aplicaciones')
      .insert({
        saldo_favor_id: saldoFavorId,
        cargo_id: cargoId,
        monto_aplicado: montoReal,
        aplicado_por: staff?.id,
        transaccion_id: transaccion.id,
      })

    // 4. Actualizar saldo a favor
    const nuevoDisponible = Number(saldo.saldo_disponible) - montoReal
    await supabase
      .from('pagos_saldo_favor')
      .update({
        monto_usado: Number(saldo.monto_usado) + montoReal,
        saldo_disponible: nuevoDisponible,
      })
      .eq('id', saldoFavorId)

    // 5. Actualizar cargo (el trigger trg_recalc_cargo ya lo hace,
    //    pero actualizamos por si el trigger no corre en tiempo)
    const nuevoPagado = Number(cargo.monto_pagado) + montoReal
    const nuevoSaldo = Number(cargo.monto_final) - nuevoPagado
    let estatus = 'pendiente'
    if (nuevoSaldo <= 0) estatus = 'pagado'
    else if (nuevoPagado > 0) estatus = 'parcial'

    await supabase
      .from('pagos_cargos')
      .update({
        monto_pagado: nuevoPagado,
        saldo: Math.max(nuevoSaldo, 0),
        estatus,
      })
      .eq('id', cargoId)

    return { error: null, montoAplicado: montoReal }
  } catch (error) {
    console.error('[aplicarSaldoFavorACargo]', error)
    return { error }
  }
}
